import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, Paper, Stack, TextField, Typography,
} from '@mui/material';

import { apiService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { cle, duServeur, useMessageTraduit } from '../utils/messageTraduit';
import LanguageSwitcher from '../components/LanguageSwitcher';
import MarqueOptimStock from '../components/commun/MarqueOptimStock';

/**
 * Le cadre des deux écrans — IDENTIQUE À CELUI DU TUNNEL ET DU LOGIN.
 *
 * ⚠️ TROISIÈME PORTE, MÊME HABILLAGE. Un prospect qui a vu le Login et un
 * client qui reçoit un lien de réinitialisation doivent arriver sur la même
 * surface : marque en haut de carte, sélecteur de langue hors de la carte, fond
 * neutre. *Une porte qui ne ressemble pas aux autres portes fait douter du
 * lien* — et c'est exactement le doute qu'un e-mail de sécurité ne doit pas
 * créer.
 */
const Cadre = ({ children }) => (
  <Box sx={{
    minHeight: '100vh', display: 'flex', alignItems: 'center',
    justifyContent: 'center', bgcolor: 'background.default', p: 2,
  }}>
    <Box sx={{ maxWidth: 480, width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <LanguageSwitcher />
      </Box>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <MarqueOptimStock />
        <Box sx={{ mt: 2 }}>{children}</Box>
      </Paper>
    </Box>
  </Box>
);

/**
 * DEMANDER un lien — et ne rien apprendre à personne.
 *
 * 🔴 L'ÉCRAN NE RÉVÈLE PAS SI LE COMPTE EXISTE, et il ne le peut pas : la
 * route rend **la même réponse dans tous les cas**, y compris sur un email
 * malformé et y compris sur une panne interne. *Une route qui rend « inconnu »
 * énumère les comptes de la plateforme, un email à la fois.*
 *
 * ⚠️ ET LE MESSAGE DE SUCCÈS EST FORMULÉ AU CONDITIONNEL — « **si** un compte
 * existe ». Écrire « un lien vous a été envoyé » serait un mensonge dans un cas
 * sur deux, et surtout **la formulation affirmative rendrait l'absence d'e-mail
 * suspecte** : l'utilisateur conclurait que son adresse n'est pas la bonne, ce
 * qui est précisément l'information qu'on refuse de donner.
 */
export const DemanderReinitialisation = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [envoye, setEnvoye] = useState(false);

  const soumettre = async (ev) => {
    ev.preventDefault();
    setEnvoi(true);
    try {
      await apiService.post('/auth/password/forgot', { email: email.trim() });
    } catch {
      // 🔴 MÊME UN ÉCHEC RÉSEAU MÈNE À L'ÉCRAN DE CONFIRMATION. Afficher une
      // erreur ici distinguerait « la requête a abouti » de « elle a échoué » —
      // et comme la route ne peut échouer que sur le chemin « compte connu »,
      // ce serait l'oracle reconstitué côté client. *L'uniformité de la réponse
      // ne vaut que si l'écran la respecte aussi.*
    } finally {
      setEnvoi(false);
      setEnvoye(true);
    }
  };

  if (envoye) {
    return (
      <Cadre>
        <Alert severity="success" sx={{ mb: 2 }}>{t('auth.resetRequested')}</Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('auth.resetRequestedHelp')}
        </Typography>
        <Button fullWidth variant="contained" onClick={() => navigate('/')}>
          {t('invitation.backToLogin')}
        </Button>
      </Cadre>
    );
  }

  return (
    <Cadre>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {t('auth.forgotHelp')}
      </Typography>
      <Box component="form" onSubmit={soumettre}>
        <Stack spacing={2}>
          <TextField
            label={t('auth.email')} type="email" value={email}
            onChange={(e) => setEmail(e.target.value)}
            required fullWidth autoFocus autoComplete="email"
            disabled={envoi}
          />
          <Button type="submit" variant="contained" size="large" fullWidth
                  disabled={envoi || !email.trim()}>
            {envoi ? <CircularProgress size={24} color="inherit" />
                   : t('auth.sendResetLink')}
          </Button>
          <Button type="button" size="small" onClick={() => navigate('/')}>
            {t('invitation.backToLogin')}
          </Button>
        </Stack>
      </Box>
    </Cadre>
  );
};

/**
 * POSER le nouveau mot de passe.
 *
 * ⚠️ CET ÉCRAN-CI PARLE, LUI. Le porteur d'un lien expiré doit savoir POURQUOI
 * il ne marche pas, sinon il recommence à l'identique. *L'oracle n'existe qu'à
 * la DEMANDE : ici, celui qui parle détient déjà un jeton.*
 */
export const PoserNouveauMotDePasse = ({ token }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [mdp, setMdp] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useMessageTraduit();

  const soumettre = async (ev) => {
    ev.preventDefault();
    // ⚠️ LA CONFIRMATION EST VÉRIFIÉE ICI ET NULLE PART AILLEURS : le serveur
    // ne la reçoit pas et n'a pas à la connaître. Même contrat qu'au tunnel.
    if (mdp !== confirmation) {
      setErreur(cle('invitation.passwordMismatch'));
      return;
    }
    setErreur(null);
    setEnvoi(true);
    try {
      await apiService.post('/auth/password/reset', { token, password: mdp });
      // 🔴 LE MESSAGE VOYAGE PAR L'ÉTAT DE NAVIGATION, pas par l'URL — même
      // décision qu'au tunnel : un paramètre traînerait dans l'historique et
      // serait partageable.
      navigate('/', { replace: true, state: { message: t('auth.resetDone') } });
    } catch (err) {
      // Le message du SERVEUR d'abord : lui seul distingue « expiré » de
      // « déjà utilisé » de « remplacé par une demande plus récente ».
      setErreur(duServeur(err, 'auth.resetFailed'));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Cadre>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {t('auth.chooseNewPassword')}
      </Typography>
      <Box component="form" onSubmit={soumettre}>
        <Stack spacing={2}>
          <TextField
            label={t('invitation.password')} type="password" value={mdp}
            onChange={(e) => setMdp(e.target.value)}
            required fullWidth autoFocus autoComplete="new-password"
            /* ⚠️ LA RÈGLE EST ÉNONCÉE, PAS DUPLIQUÉE : son AUTORITÉ reste le
               serveur (`utils/password_policy`). L'afficher évite seulement de
               la découvrir après avoir soumis. */
            helperText={t('invitation.passwordHint')}
            disabled={envoi}
          />
          <TextField
            label={t('invitation.passwordConfirm')} type="password"
            value={confirmation} onChange={(e) => setConfirmation(e.target.value)}
            required fullWidth autoComplete="new-password" disabled={envoi}
            error={Boolean(confirmation) && confirmation !== mdp}
          />
          {erreur && <Alert severity="error">{erreur}</Alert>}
          <Button type="submit" variant="contained" size="large" fullWidth
                  disabled={envoi || !mdp || !confirmation}>
            {envoi ? <CircularProgress size={24} color="inherit" />
                   : t('auth.resetSubmit')}
          </Button>
        </Stack>
      </Box>
    </Cadre>
  );
};
